import type {
    CSSProperties,
    FC,
    FormEvent,
    ReactNode
} from 'react'

import { useId, useState } from 'react'
import { ArrowRight, Check } from 'lucide-react'
import { isEmail } from '@/lib/format'
import { cn } from '@/lib/utils'

interface SubscribeFormProps {
    source: string
    tone?: 'acid' | 'ink'
    className?: string
}

type Status = 'idle' | 'sending' | 'done' | 'error'

/** Email sign-up for "The List". Saves the address so the studio can reach out before each drop. */
const SubscribeForm: FC<SubscribeFormProps> = ({ source, tone = 'acid', className }): ReactNode => {
    const id = useId()
    const [email, setEmail] = useState('')
    const [company, setCompany] = useState('')
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')

    const onAcid = tone === 'acid'

    const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (!isEmail(email)) {
            setStatus('error')
            setMessage('Please enter a valid email address.')

            return
        }

        setStatus('sending')
        setMessage('')

        try {
            const response = await fetch('/api/subscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, source, company })
            })

            const payload = await response.json().catch(() => null) as { message?: string } | null

            if (!response.ok) throw new Error(payload?.message || 'Something went wrong.')

            setStatus('done')
            setMessage("You’re on the list. We’ll write before the next drop.")
            setEmail('')
        } catch (error) {
            setStatus('error')
            setMessage(error instanceof Error ? error.message : 'Something went wrong. Please try again.')
        }
    }

    return (
        <form onSubmit={submit} noValidate className={cn('w-full', className)}>
            <div
                className={cn(
                    'flex items-center gap-3 border-b pb-2 transition-colors duration-300',
                    onAcid ? 'border-ink/35 focus-within:border-ink' : 'border-line focus-within:border-bone'
                )}
            >
                <label htmlFor={`${id}-email`} className='sr-only'>
                    Email address
                </label>

                <input
                    id={`${id}-email`}
                    type='email'
                    name='email'
                    autoComplete='email'
                    required
                    value={email}
                    onChange={event => setEmail(event.target.value)}
                    placeholder='you@domain.com'
                    aria-invalid={status === 'error'}
                    aria-describedby={`${id}-status`}
                    className={cn(
                        'h-12 min-w-0 flex-1 bg-transparent text-lg outline-none',
                        onAcid ? 'text-ink placeholder:text-ink/45' : 'text-bone placeholder:text-bone/40'
                    )}
                />

                <input
                    type='text'
                    name='company'
                    tabIndex={-1}
                    autoComplete='off'
                    value={company}
                    onChange={event => setCompany(event.target.value)}
                    aria-hidden='true'
                    className='absolute h-0 w-0 opacity-0'
                />

                <button
                    type='submit'
                    disabled={status === 'sending'}
                    className={cn(
                        'wipe roll-host group inline-flex h-12 shrink-0 items-center gap-3 px-5 label-mono transition-colors duration-500 disabled:opacity-60',
                        onAcid ? 'bg-ink text-bone hover:text-ink' : 'bg-acid text-ink hover:text-acid'
                    )}
                    style={{ '--wipe-color': onAcid ? 'var(--bone)' : 'var(--ink)' } as CSSProperties}
                >
                    {status === 'sending' ? 'Sending' : status === 'done' ? 'Subscribed' : 'Subscribe'}

                    {status === 'done'
                        ? <Check aria-hidden='true' className='size-4' />
                        : <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-500 group-hover:translate-x-1' />}
                </button>
            </div>

            <p
                id={`${id}-status`}
                role='status'
                aria-live='polite'
                className={cn('mt-3 min-h-5 text-sm', onAcid ? 'text-ink/75' : 'text-bone/70', status === 'error' && (onAcid ? 'text-ink' : 'text-destructive'))}
            >
                {message}
            </p>
        </form>
    )
}

export default SubscribeForm
